import { h, Component} from 'preact';
import cx from 'classnames';
import { Subject } from 'rxjs';

import { LeaseTerms, FurnishingChoices, ConditionChoices } from '../../../../../constants';
import { restroomTypes } from '../../../../../constants/properties';
import { Listing } from '../../../../../types';
import { Form } from '../../../../utils/forms/form';
import { cxValidateField } from '../../../../utils/forms/validators';
import hopplerLinkState, { hopplerUpdateFormFieldValue } from '../../../../utils/forms/hoppler-linkstate';
import { formatForNativeDateInput } from '../../../../core/date-formatter';
import { options } from '../../../../../constants/options';

import { CurrencyInput } from '../../common/hoppler-currency-input.component';
import { NumberInput } from '../../common/hoppler-number-input.component';
import { ListingEditorErrorMessage } from './error-message.component';
import { DatePickerControl } from '../../common/date-picker-control.component';
import { LabeledChoiceButtonGroup } from '../../common/labeled-choice-button-group.component';

interface ListingEditorEssentialSectionProps {
  isPending?: number;
  form?: Form;
  requiresBuilding?: boolean;
  formSubject?: Subject<Form>;
  listing?: Listing;
}

interface ListingEditorEssentialSectionState {
  discountedPercentage?: number;
}

export class ListingEditorEssentialSection extends Component<ListingEditorEssentialSectionProps, ListingEditorEssentialSectionState> {
  constructor(props: ListingEditorEssentialSectionProps) {
    super(props);
    let discount = this.calculateDiscountPercentage(this.props.form.fields['discountedPrice'].value);
    this.setState({
      discountedPercentage: +discount.toFixed(2)
    });
  }

  calculateDiscountPercentage = (discount) => {
    let price = this.props.form.fields['salePrice'].value;
    if (this.props.form.fields['category'].value === 'Rent') {
      price = this.props.form.fields['rentalPrice'].value;
    };
    return 100 - ((discount / price) * 100);
  }

  ondateAvailabilitySelected = (value) => {
    hopplerUpdateFormFieldValue(this.props.formSubject, this.props.form, 'dateAvailability', value);
  }

  onRentalPriceChange = (value) => {
    hopplerUpdateFormFieldValue(this.props.formSubject, this.props.form, 'rentalPrice', value);
  }
  
  onPricePerSqmChange = (value) => {
    hopplerUpdateFormFieldValue(this.props.formSubject, this.props.form, 'pricePerSqm', value);
  }

  onAssocDuesChange = (value) => {
    hopplerUpdateFormFieldValue(this.props.formSubject, this.props.form, 'associationDues', value);
  }

  onFloorAreaChange = (value) => {
    hopplerUpdateFormFieldValue(this.props.formSubject, this.props.form, 'floorArea', value);
  }

  onLotAreaChange = (value) => {
    hopplerUpdateFormFieldValue(this.props.formSubject, this.props.form, 'lotArea', value);
  }

  onSalePriceChange = (value) => {
    hopplerUpdateFormFieldValue(this.props.formSubject, this.props.form, 'salePrice', value);
  }

  handleUpdateFormFieldValue(fieldName: string, value: any) {
    hopplerUpdateFormFieldValue(this.props.formSubject, this.props.form, fieldName, value);
  }

  onDiscountPriceChange = (value) => {
    let discount = this.calculateDiscountPercentage(value);
    this.setState({
      discountedPercentage: +discount.toFixed(2)
    });
    hopplerUpdateFormFieldValue(this.props.formSubject, this.props.form, 'discountedPrice', value);
  }

  // JSX Blocks
  getPricePerSqmBlock(props: ListingEditorEssentialSectionProps) {
    const { isPending, form, listing } = props;

    return (
      <div className="d-flex flex-column hi-input-group">
        <label className="hi-input-group__label">Price per Sqm*</label>

        <div className="flex-fill" />

        <CurrencyInput
          disabled={ isPending == 1 || !listing.canEdit }
          onValueChange={ this.onPricePerSqmChange }
          value={ form.fields['pricePerSqm'].value }
          fieldName="pricePerSqm"
          placeholder="Price per Sqm"
          min={ 0 }
          isInvalid={ cxValidateField(form.fields['pricePerSqm']) }>
        </CurrencyInput>

        <ListingEditorErrorMessage
          form={ form }
          fieldName="pricePerSqm"
          message="Please add a price per sqm"
        />

      </div>
    );
  }

  getAssociationDuesBlock(props: ListingEditorEssentialSectionProps) {
    const listing = props.listing;
    const form = props.form;
    const assocDuesIncluded = form.fields.associationDuesIncluded.value == 1;
    const forRent = listing.category === 'Rent';

    // IT-1617, IT-1604 & IT-1620
    // Do not show assoc dues if for sale
    // 
    // If association dues are *not* included, they must be paid separately. We
    // must ask about the amount of this separate payment.
    if (forRent && !assocDuesIncluded) {
      return (
        <div className="col-12">
          <div className="row">
            <div className="col-12 mt-3">
              <div className="hi-input-group">
                <label className="hi-input-group__label">Association Dues*</label>
                  <CurrencyInput
                    disabled={ props.isPending == 1 || !listing.canEdit }
                    onValueChange={ value => hopplerUpdateFormFieldValue(props.formSubject, props.form, 'associationDues', value) }
                    value={ form.fields['associationDues'].value }
                    fieldName="associationDues"
                    placeholder="Association Dues"
                    min={ 0 }
                    isInvalid={ cxValidateField(form.fields['associationDues']) }
                  />
              </div>
  
              <ListingEditorErrorMessage
                form={ form }
                fieldName="associationDues"
                message="Please provide association dues"
              />
            </div>
          </div>
        </div>
      ); 
    }
  }

  getEssentialsDiscountlock(props: ListingEditorEssentialSectionProps, state: ListingEditorEssentialSectionState) {
    const { listing, form, isPending } = props;
    return (
      <div className="row">
        {
          <div className="col-6">
            <div className="hi-input-group">
              <label className="hi-input-group__label">Discounted Price</label>
                <NumberInput
                  disabled={ isPending == 1 || !listing.canEdit }
                  onValueChange={ this.onDiscountPriceChange }
                  value={ form.fields['discountedPrice'].value }
                  fieldName="discountedPrice"
                  placeholder="PHP"
                  min={ 0 }
                  isInvalid={ cxValidateField(form.fields['discountedPrice']) }>
                </NumberInput>
            </div>

            <ListingEditorErrorMessage
              form={ form }
              fieldName="discountedPrice"
              message="Please add a discount price"
            />
          </div>
        }

        {
          <div className="col-6">
            <div className="hi-input-group">
              <label className="hi-input-group__label">Discount Percentage (%)</label>
                <input
                  disabled
                  value={ state.discountedPercentage }
                  className="form-control hi-text-input"
                  placeholder="%"
                  min={ 0 }
                  type="text"
                />
            </div>
          </div>
        }
      </div>
    );
  }

  getEssentialsPriceBlock(props: ListingEditorEssentialSectionProps) {
    const { listing, form, isPending } = props;
    const category = form.fields.category.value;
    const division = form.fields.division.value;

    if (division === 'Commercial') {
      return this.getPricePerSqmBlock(props);
    } else if (category === 'Rent') {
      return (
        <div className="d-flex flex-column hi-input-group">
          <label className="hi-input-group__label">Monthly Rental Price</label>

          <div className="flex-fill" />

          <CurrencyInput
            disabled={ isPending == 1 || !listing.canEdit }
            onValueChange={ this.onRentalPriceChange }
            value={ form.fields['rentalPrice'].value }
            fieldName="rentalPrice"
            placeholder="Rental Price"
            min={ 0 }
            isInvalid={ cxValidateField(form.fields['rentalPrice']) }>
          </CurrencyInput>

          <ListingEditorErrorMessage
            form={ form }
            fieldName="rentalPrice"
            message="Please add a rental price"
          />  
        </div>
      );
    } else { // Residential, Sale
      return (
        <div className="d-flex flex-column">
          <div className="hi-input-group">
            <label className="hi-input-group__label">Sale Price</label>

            <CurrencyInput
              disabled={ isPending == 1 || !listing.canEdit }
              onValueChange={ this.onSalePriceChange }
              value={ form.fields['salePrice'].value }
              fieldName="salePrice"
              placeholder="Sale Price"
              min={ 0 }
              isInvalid={ cxValidateField(form.fields['salePrice']) }>
            </CurrencyInput>
          </div>

          <ListingEditorErrorMessage
            form={ form }
            fieldName="salePrice"
            message="Please add a sale price"
          />
        </div>
      );
    }
  }

  getMinLeaseTermBlock(props: ListingEditorEssentialSectionProps) {
    const { form, listing, formSubject, isPending } = props; 
    
    if (form.fields['category'].value === 'Rent') {
      return (
        <div className="row">
          <div className="col-12">
            <div className="hi-input-group">
              <label className="hi-input-group__label">Minimum Lease Term (in years)*</label>
              <LabeledChoiceButtonGroup
                choices={ LeaseTerms }
                onSelect={ value => hopplerUpdateFormFieldValue(formSubject, form, 'minLeaseTerm', value) }
                value={ form.fields.minLeaseTerm.value } />
              <ListingEditorErrorMessage
                form={ form }
                fieldName="minLeaseTerm"
                message="Please select a lease term"
              />

            </div>
          </div>
        </div>
      );
    }
  }

  getEssentialsAreaBlock(props: ListingEditorEssentialSectionProps) {
    const { 
      form,
      isPending,
      listing,
      requiresBuilding
    } = props;

    const noFloorArea = ['Vacant Lot', 'Commercial Lot']
    
    return (
      <div className="row">
        {
          noFloorArea.includes(listing.type) ? null :
            <div className="col">
              <div className="hi-input-group">
                <label className="hi-input-group__label">Floor Area*</label>
                  <NumberInput
                    disabled={ isPending == 1 || !listing.canEdit }
                    onValueChange={ this.onFloorAreaChange }
                    value={ form.fields['floorArea'].value }
                    fieldName="floorArea"
                    placeholder="Floor Area"
                    min={ 0 }
                    isInvalid={ cxValidateField(form.fields['floorArea']) }>
                  </NumberInput>
              </div>

              <ListingEditorErrorMessage
                form={ form }
                fieldName="floorArea"
                message="Please add a floor area"
              />
            </div>
        }

        {
          !requiresBuilding ? 
            <div className="col">
              <div className="hi-input-group">
                <label className="hi-input-group__label">Lot Area*</label>
                  <NumberInput
                    disabled={ isPending == 1 || !listing.canEdit }
                    onValueChange={ this.onLotAreaChange }
                    value={ form.fields['lotArea'].value }
                    fieldName="lotArea"
                    placeholder="Lot Area"
                    min={ 0 }
                    isInvalid={ cxValidateField(form.fields['lotArea']) }>
                  </NumberInput>
              </div>

              <ListingEditorErrorMessage
                form={ form }
                fieldName="lotArea"
                message="Please add a lot area"
              />

            </div> : null
        }
      </div>
    );
  }

  getEssentialsFurnishingBlock(props: ListingEditorEssentialSectionProps) {
    const { listing, formSubject, form, isPending } = props;
    const isResidential = listing.division === 'Residential';
    const isVacantLot = listing.type === 'Vacant Lot';
    const furnishField = form.fields.furnishing;

    if (isResidential && !isVacantLot) {
      // console.log(form.fields.furnishing, form.fields.furnishing.value === FurnishingChoices[0].value);
      return (
        <div className="row mt-4">
          <div className="col-12">
            <div className="hi-input-group">
              <label className="hi-input-group__label">Furnishing*</label>
              <div className="hi-dash-listings__furnishing-options mb-1">
                {
                  FurnishingChoices.map((option) => 
                    <span className="hi-input-group__checkbox">
                      <label className="mb-0">
                        {/*
                          INTENTIONALLY omitted the "name" attribute on this
                          field due to a weird bug where these radios go blank
                          when assocDuesIncluded is set to "No"
                        */}
                        <input
                          disabled={ isPending == 1 || !listing.canEdit }
                          type="radio"
                          onChange={ hopplerLinkState(formSubject, form, 'furnishing') }
                          value={ option.value } 
                          checked={ furnishField.value == option.value }
                        />
                        { option.label }
                      </label>
                    </span>
                  )
                }
              </div>

              <ListingEditorErrorMessage
                form={ form }
                fieldName="furnishing"
                message="Please select a furnishing"
              />

            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="row mt-4">
          <div className="col-12">
            <div className="hi-input-group">
              <label className="hi-input-group__label">Condition*</label>
              <div className="hi-dash-listings__condition-options mb-1">
                {
                  ConditionChoices.map((option) => 
                  <span className="hi-input-group__checkbox m-0">
                    <label>
                      <input
                        disabled={ isPending == 1 || !listing.canEdit }
                        type="radio" name="condition" 
                        onChange={ hopplerLinkState(formSubject, form, 'condition') }
                        value={option.value} 
                        checked={form.fields['condition'].value == option.value}
                        />
                      { option.label }
                    </label>
                  </span>
                  )
                }
              </div>

              <ListingEditorErrorMessage
                form={ form }
                fieldName="condition"
                message="Please select a condition"
              />

            </div>
          </div>
        </div>
      )
    }

  }

  getEssentialsBedroomBlock(props: ListingEditorEssentialSectionProps) {
    const { form, isPending, listing, formSubject } = props;

    const noStudio: string[] = ['house and lot', 'townhouse'];
    const noBedrooms = ['vacant lot']
    
    if (
      form.fields['division'].value === 'Residential' &&
      !noBedrooms.includes(listing.type.toLowerCase()) 
    ) {
      return (
        <div className="row">
          <div className="col-12">
            <div className="hi-input-group">
              <label className="hi-input-group__label">Bedrooms*</label>
              <div className="d-flex mb-1">
                <select
                  name="bedrooms" className="form-control"
                  onChange={ hopplerLinkState(formSubject, form, 'bedrooms') }
                  value={ form.fields['bedrooms'].value }
                >
                  { noStudio.includes(listing.type.toLowerCase()) ? null : (
                    <option value={ 0 }>Studio</option>
                  )} 
                  
                  { Array.from({ length: 99 }, (_, i) => i + 1).map(i => (
                    <option value={ i }>{ i }</option>
                  )) }
                </select>
              </div>

              <ListingEditorErrorMessage
                form={ form }
                fieldName="bedrooms"
                message="Please select a bedroom"
              />

            </div>
          </div>
        </div>
      );
    }
  }

  getRestRoomBlock(props: ListingEditorEssentialSectionProps) {
    const { form, listing, formSubject, isPending } = props;
    const noRestroom = ['vacant lot']    

    if (listing.division == 'Commercial') {
      return (
        <div className="hi-input-group">
          <label className="hi-input-group__label">Restroom Type*</label>
          <select 
            className="form-control"
            disabled={ isPending == 1 || !listing.canEdit }
            value={ form.fields['restroom'].value }
            onChange={ hopplerLinkState(formSubject, form, 'restroom') }
            name="restRoom">
            <option className="text-muted" value="" disabled={true} selected>Choose Restroom Type</option>
            {
              restroomTypes.map((item, i)=> (
              <option
                key={ i }
                name={ item }
                value={ item }>{ item }</option>
              ))
            }
          </select>
        </div>
      )
    }
  }

  getBathroomBlock(props: ListingEditorEssentialSectionProps) {
    const { form, isPending, listing, formSubject, } = props;
    const noRestroom = ['vacant lot']
    
    if (!noRestroom.includes(listing.type.toLowerCase())) {
      return (
        <div className="hi-input-group">
          <label className="hi-input-group__label">{ 
            listing.division == 'Residential' ? 'Bathroom*' : 'Restrooms*' 
          }</label>
          <div className="d-flex mb-1">
            {
              [1,2,3,4,5].map((option) => 
              <span className="hi-input-group__checkbox mr-3">
                <label>
                  <input
                    disabled={ isPending == 1 || !listing.canEdit }
                    type="radio" name="bathrooms" 
                    onChange={ hopplerLinkState(formSubject, form, 'bathrooms') }
                    value={option} 
                    checked={form.fields['bathrooms'].value == option}
                    />
                  { option > 4 ? '5+' : option }
                </label>
              </span>
              )
            }
          </div>
  
          <ListingEditorErrorMessage
            form={ form }
            fieldName="bathrooms"
            message="Please select bathroom count'"
          />
  
        </div>
      )
    }
  }

  onAssocDuesIncludedChange(choice) {
    hopplerUpdateFormFieldValue(this.props.formSubject, this.props.form, 'associationDuesIncluded', choice);
  }

  render(props: ListingEditorEssentialSectionProps, state: ListingEditorEssentialSectionState) {
    const {
      form,
      isPending,
      listing,
      formSubject,
    } = this.props;

    const divisions = ['Commercial', 'Residential']

    return (
      <div className="row mt-4">
        <div className="col-12 mt-3">
          { this.getEssentialsPriceBlock(props) }
        </div>
        { !(listing.preSale == 1) && (options.discountEnabled) &&
          <div className="col-12 mt-3">
            { this.getEssentialsDiscountlock(props, state) }
          </div>
        }
        <div className="col-12 mt-3">
          { this.getMinLeaseTermBlock(props) }
        </div>
  
        <div className="col-12 mt-4 hi-form-grid">
          <div className="d-flex flex-column justify-content-between">
            <label className="hi-input-label">
              Can the property owner issue an official receipt? *
            </label>
            <div className="flex-fill" />
            <LabeledChoiceButtonGroup
              choices={ [
                { label: 'Yes', value: 1 },
                { label: 'No', value: 0 }
              ] }
              onSelect={ value => hopplerUpdateFormFieldValue(formSubject, form, 'ownerReceipt', value) }
              value={ form.fields.ownerReceipt.value } />

            <ListingEditorErrorMessage
              form={ form }
              fieldName="ownerReceipt"
              message="Please choose if the owner provides receipts or not" />
          </div>

          {
            form.fields['category'].value === 'Rent' ?
              <div className="d-flex flex-column justify-content-between">
                <label className="hi-input-label">
                  Are the association dues included in the rental price? *
                </label>

                <div className="flex-fill" />

                <LabeledChoiceButtonGroup
                  choices={ [
                    { label: 'Yes', value: 1 },
                    { label: 'No', value: 0 },
                  ] }
                  value={ form.fields.associationDuesIncluded.value }
                  onSelect={ choice => hopplerUpdateFormFieldValue(formSubject, form, 'associationDuesIncluded', choice) } />
                
                <ListingEditorErrorMessage
                  form={ form }
                  fieldName="associationDuesIncluded"
                  message="Please choose if Association dues are included" />
              </div> : null
          }
        </div>

        { this.getAssociationDuesBlock(props) }

        <div className="col-12 mt-3">
          { this.getEssentialsAreaBlock(props) }
        </div>        

        <div className="col-12 mt-3">
          { this.getEssentialsFurnishingBlock(props) }
        </div>

        <div className="col-12 mt-3">
          { this.getEssentialsBedroomBlock(props) }
        </div>

        <div className="col-12 mt-3">
          { this.getRestRoomBlock(props) }
        </div>

        <div className="col-12 mt-3">
          { this.getBathroomBlock(props) }
        </div>

        <div className="col-12 mt-4">
          <div className="form-group">
            <label className="hi-input-group__label">Availability Date*</label>
            <DatePickerControl
              disabled={ isPending == 1 || !listing.canEdit }
              onChange={ evt => this.ondateAvailabilitySelected(evt.target['value']) }
              value={ formatForNativeDateInput(form.fields['dateAvailability'].value) }
              forceFallbackInput={ true }
              className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields['dateAvailability'])
              }) } />
          </div>
        </div>
        
      </div>
    )
  }
}